import React from 'react';
import { Box, Button, Chip, Collapse, Dialog, DialogTitle, DialogContent, DialogActions, Divider, FormControl, MenuItem, Select, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getStudyPriors } from '../api';
import { computeMonitorTopologyFingerprint, createMonitorWindowHandle, detectMonitorTopologyMismatch, detectWindowPlacementSupport, launchSavedMonitorWorkspace, loadProfiles, requestScreenDetails, saveProfiles, createDefaultProfile } from '../multiMonitor';

const mono = { fontFamily: 'monospace', fontSize: 11 };
const roleOptions = [
  { value: 'primary', label: 'Current study' },
  { value: 'comparison', label: 'Comparison series' },
  { value: 'priors', label: 'Prior exam' },
  { value: 'mpr', label: 'MPR / 3D' },
  { value: 'worklist', label: 'Worklist / notes' },
  { value: 'disabled', label: 'Off / unused' },
];

const roleLabel = (value) => roleOptions.find(option => option.value === value)?.label || value || 'Current study';

const formatValue = (value) => (value === undefined || value === null || value === '' ? 'unknown' : String(value));

const screenName = (monitor, index) => {
  const descriptor = monitor?.descriptor || {};
  const name = descriptor.label ? ` - ${descriptor.label}` : '';
  return `Screen ${index + 1}${descriptor.isPrimary ? ' (primary)' : ''}${name}`;
};

const monitorDetails = (monitor) => {
  const descriptor = monitor?.descriptor || {};
  const placement = monitor?.placement || {};
  return [
    ['Browser label', descriptor.label || 'Not provided'],
    ['Screen size', `${formatValue(descriptor.width)} x ${formatValue(descriptor.height)}`],
    ['Available area', `${formatValue(descriptor.availWidth)} x ${formatValue(descriptor.availHeight)}`],
    ['Screen position', `${formatValue(descriptor.left)}, ${formatValue(descriptor.top)}`],
    ['Available position', `${formatValue(descriptor.availLeft)}, ${formatValue(descriptor.availTop)}`],
    ['Saved window', `${formatValue(placement.width)} x ${formatValue(placement.height)} at ${formatValue(placement.left)}, ${formatValue(placement.top)}`],
    ['Primary / internal', `${descriptor.isPrimary ? 'Primary' : 'Not primary'} / ${descriptor.isInternal ? 'Internal' : 'External or unknown'}`],
    ['Pixel ratio', formatValue(descriptor.devicePixelRatio)],
    ['Monitor id', monitor?.id || 'unknown'],
  ];
};

const sameScreen = (a = {}, b = {}) => (
  a && b
  && a.left === b.left
  && a.top === b.top
  && a.width === b.width
  && a.height === b.height
);

const screenIdentity = (screen = {}) => [
  screen.label || '',
  screen.left,
  screen.top,
  screen.width,
  screen.height,
  screen.availLeft,
  screen.availTop,
  screen.availWidth,
  screen.availHeight,
].map(value => value === undefined || value === null ? '' : String(value)).join('|');

const uniqueScreens = (screens = []) => {
  const seen = new Set();
  return screens.filter((screen) => {
    const key = screenIdentity(screen);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const hasScreenGeometry = (screen = {}) => (
  screen.left !== undefined
  && screen.top !== undefined
  && screen.width !== undefined
  && screen.height !== undefined
);

const uniqueMonitors = (monitors = []) => {
  const seen = new Set();
  return monitors.filter((monitor, idx) => {
    const descriptor = monitor?.descriptor || {};
    const key = hasScreenGeometry(descriptor) ? screenIdentity(descriptor) : (monitor?.id || `monitor-${idx}`);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const MultiMonitorDialog = ({ open, onClose, studyInstanceUID, workspaceId, monitorId, role }) => {
  const [support, setSupport] = React.useState(() => detectWindowPlacementSupport());
  const [screenDetails, setScreenDetails] = React.useState(null);
  const [profiles, setProfiles] = React.useState(null);
  const [status, setStatus] = React.useState('');
  const [detailsOpen, setDetailsOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setSupport(detectWindowPlacementSupport());
    loadProfiles().then(setProfiles).catch(() => setProfiles({ activeProfileId: 'default', profiles: [createDefaultProfile()] }));
  }, [open]);

  const activeProfileRaw = profiles?.profiles?.find(p => p.id === profiles.activeProfileId) || profiles?.profiles?.[0];
  const activeProfile = React.useMemo(() => (
    activeProfileRaw
      ? { ...activeProfileRaw, monitors: uniqueMonitors(activeProfileRaw.monitors || []) }
      : activeProfileRaw
  ), [activeProfileRaw]);
  const topologyMismatch = React.useMemo(() => detectMonitorTopologyMismatch(activeProfile, {
    screens: uniqueScreens(screenDetails?.screens || []),
    platform: navigator.platform || 'unknown',
    devicePixelRatio: window.devicePixelRatio || 1,
  }), [activeProfile, screenDetails]);

  const buildProfileFromScreens = () => {
    const baseProfile = activeProfile || createDefaultProfile();
    const screens = uniqueScreens(screenDetails?.screens || []);
    const existingMonitors = baseProfile.monitors?.length ? baseProfile.monitors : createDefaultProfile().monitors;
    const hasDetectedScreens = screens.length > 0;
    if (!hasDetectedScreens) {
      return {
        ...baseProfile,
        updatedAt: new Date().toISOString(),
        monitors: uniqueMonitors(existingMonitors),
      };
    }
    const monitorCount = hasDetectedScreens ? screens.length : Math.max(existingMonitors.length, 2);
    const usedExisting = new Set();
    const findExistingMonitor = (descriptor, idx, detectedId) => {
      const matchIndex = existingMonitors.findIndex((monitor, existingIdx) => (
        !usedExisting.has(existingIdx)
        && (
          sameScreen(monitor.descriptor, descriptor)
          || monitor.id === detectedId
          || (!hasDetectedScreens && existingIdx === idx)
        )
      ));
      if (matchIndex >= 0) {
        usedExisting.add(matchIndex);
        return existingMonitors[matchIndex] || {};
      }
      usedExisting.add(idx);
      return existingMonitors[idx] || {};
    };
    const monitors = Array.from({ length: monitorCount }, (_, idx) => {
      const detectedDescriptor = screens[idx];
      const descriptor = detectedDescriptor || existingMonitors[idx]?.descriptor || {};
      const isCurrentScreen = hasDetectedScreens
        ? sameScreen(descriptor, screenDetails?.currentScreen)
        : idx === 0 || descriptor.isPrimary;
      const detectedId = isCurrentScreen ? 'primary' : `diagnostic-${idx + 1}`;
      const existing = findExistingMonitor(descriptor, idx, detectedId);
      return {
        ...existing,
        id: hasDetectedScreens ? detectedId : (existing.id || detectedId),
        label: hasDetectedScreens ? screenName({ descriptor }, idx) : (existing.label || (isCurrentScreen ? 'Primary viewer' : `Diagnostic monitor ${idx + 1}`)),
        role: existing.role || (isCurrentScreen ? 'primary' : 'comparison'),
        descriptor,
        placement: isCurrentScreen
          ? { left: window.screenX, top: window.screenY, width: window.outerWidth, height: window.outerHeight }
          : {
              left: descriptor.left ?? descriptor.availLeft ?? existing.placement?.left,
              top: descriptor.top ?? descriptor.availTop ?? existing.placement?.top,
              width: descriptor.width ?? descriptor.availWidth ?? existing.placement?.width,
              height: descriptor.height ?? descriptor.availHeight ?? existing.placement?.height,
            },
        viewportLayout: existing.viewportLayout || { rows: 2, cols: 2 },
        matchHints: existing.matchHints || [],
      };
    });
    return {
      ...baseProfile,
      updatedAt: new Date().toISOString(),
      workstationFingerprint: computeMonitorTopologyFingerprint({ screens, platform: navigator.platform || 'unknown', devicePixelRatio: window.devicePixelRatio || 1 }),
      monitors,
    };
  };

  const handleDetectScreens = async () => {
    setStatus('Requesting screen details…');
    try {
      const details = await requestScreenDetails();
      setScreenDetails(details);
      setStatus(details ? 'Screen details captured for this browser session.' : 'Window Placement API is not available in this browser.');
    } catch (e) {
      setStatus(e?.message || 'Screen permission was not granted.');
    } finally {
      setSupport(detectWindowPlacementSupport());
    }
  };

  const handleSaveProfile = async () => {
    const nextProfile = buildProfileFromScreens();
    const next = { activeProfileId: nextProfile.id, profiles: [nextProfile, ...(profiles?.profiles || []).filter(p => p.id !== nextProfile.id)] };
    const saved = await saveProfiles(next);
    setProfiles(saved);
    setStatus('Monitor choices saved. Use Open saved workspace to launch this layout.');
  };

  const handleRoleChange = (targetMonitorId, nextRole) => {
    const baseProfile = activeProfile || createDefaultProfile();
    const monitors = (baseProfile.monitors || []).map((monitor, idx) => (
      monitor.id === targetMonitorId
        ? { ...monitor, role: nextRole, label: screenName(monitor, idx), updatedAt: new Date().toISOString() }
        : monitor
    ));
    const nextProfile = { ...baseProfile, monitors, updatedAt: new Date().toISOString() };
    setProfiles({
      activeProfileId: nextProfile.id,
      profiles: [nextProfile, ...(profiles?.profiles || []).filter(p => p.id !== nextProfile.id)],
    });
    setStatus('Choice updated. Save the monitor profile to keep it.');
  };

  const resolveStudyForRole = React.useCallback(async (targetRole) => {
    if (targetRole !== 'priors') return { studyUID: studyInstanceUID, message: '' };
    try {
      const priorMatches = await getStudyPriors(studyInstanceUID, { limit: 1 });
      const prior = priorMatches?.[0];
      if (prior?.studyInstanceUID) {
        return { studyUID: prior.studyInstanceUID, message: 'Prior exam found and opened.' };
      }
      return { studyUID: null, message: 'No same-patient prior study was found for the prior monitor.' };
    } catch (e) {
      return { studyUID: null, message: `Could not check priors: ${e?.response?.data?.detail || e.message || 'unknown error'}` };
    }
  }, [studyInstanceUID]);

  const handleOpen = async (targetMonitor = 'diagnostic-2', targetRole = 'comparison') => {
    const target = activeProfile?.monitors?.find(m => m.id === targetMonitor);
    if (target?.role === 'disabled' || targetRole === 'disabled') {
      setStatus(`${target?.label || targetMonitor} is set to Off / unused.`);
      return;
    }
    setStatus(`Opening ${roleLabel(targetRole)} on ${target?.label || targetMonitor}…`);
    const reserved = createMonitorWindowHandle({ workspaceId, monitorId: targetMonitor, monitor: target });
    if (!reserved.child) {
      setStatus('Pop-up was blocked. Use your browser pop-up control or the Open button for that row.');
      return;
    }
    const resolved = await resolveStudyForRole(targetRole);
    if (!resolved.studyUID) {
      try { reserved.child.close(); } catch (_) {}
      setStatus(resolved.message);
      return;
    }
    const opened = reserved.navigate(resolved.studyUID, targetRole);
    const roleNote = targetRole === 'comparison' ? ' Comparison uses the current study series for now.' : '';
    setStatus(opened ? `Opened ${targetMonitor}${target ? ' using saved placement when the browser allows it.' : '. Move it to the desired monitor, detect screens, then save.'}${resolved.message ? ` ${resolved.message}` : ''}${roleNote}` : 'Pop-up was blocked. Use your browser pop-up control or the Open button for that row.');
  };

  const handleOpenWorkspace = async () => {
    const targets = (activeProfile?.monitors || []).filter(m => m.role !== 'disabled');
    if (!targets.length) {
      setStatus('No enabled monitors are saved in this profile yet.');
      return;
    }
    setStatus('Opening saved monitor workspace…');
    const { opened, reusedCurrent, skipped } = await launchSavedMonitorWorkspace({
      profile: activeProfile,
      studyInstanceUID,
      workspaceId,
      monitorId,
      role,
      resolveStudyForRole,
    });
    const total = opened + reusedCurrent;
    const pieces = [];
    if (total) pieces.push(`Activated ${total} monitor${total === 1 ? '' : 's'}${reusedCurrent ? ` including the current window` : ''}.`);
    if (skipped.length) pieces.push(skipped.join(' '));
    if (targets.some(t => t.role === 'comparison')) pieces.push('Comparison opens the current study series for now.');
    setStatus(pieces.join(' ') || 'Pop-ups were blocked. Allow pop-ups for this site and try again.');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: '#101418', color: '#d8e8ef' } }}>
      <DialogTitle sx={{ pb: 1 }}>Multi-monitor workspace</DialogTitle>
      <DialogContent dividers sx={{ borderColor: '#263238' }}>
        <Stack spacing={2}>
          <Box>
            <Typography sx={{ fontSize: 13, color: '#c8d7df' }}>
              Choose what each monitor should show, then save the profile for this workstation.
            </Typography>
            <Typography sx={{ ...mono, mt: 0.75, color: '#78909c' }}>Current window: {roleLabel(role)} • {monitorId}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
              <Chip size="small" label={`BroadcastChannel ${support.broadcastChannel ? 'ready' : 'unavailable'}`} color={support.broadcastChannel ? 'success' : 'default'} />
              <Chip size="small" label={`Window Placement API ${support.getScreenDetails ? 'available' : 'unavailable'}`} color={support.getScreenDetails ? 'success' : 'default'} />
              <Chip size="small" label={support.isExtended === null ? 'extended screen unknown' : `extended screen ${support.isExtended ? 'yes' : 'no'}`} />
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" onClick={handleOpenWorkspace} startIcon={<OpenInNewIcon />} sx={{ borderRadius: 0 }}>Open saved workspace</Button>
            <Button variant="outlined" size="small" onClick={handleDetectScreens} disabled={!support.getScreenDetails} sx={{ borderRadius: 0 }}>Detect screens</Button>
            <Button variant="contained" size="small" onClick={handleSaveProfile} sx={{ borderRadius: 0 }}>Save monitor profile</Button>
          </Box>

          {status && <Typography sx={{ fontSize: 12, color: '#90caf9' }}>{status}</Typography>}


          {topologyMismatch.mismatched && (
            <Box sx={{ border: '1px solid #ff9800', bgcolor: 'rgba(255,152,0,0.10)', p: 1.25 }}>
              <Typography sx={{ fontSize: 12, color: '#ffcc80', fontWeight: 700 }}>Different workstation/monitor layout detected</Typography>
              <Typography sx={{ fontSize: 11, color: '#ffe0b2', mt: 0.5 }}>
                This saved profile was captured with {topologyMismatch.savedCount || '?'} screen{topologyMismatch.savedCount === 1 ? '' : 's'}, but this workstation currently reports {topologyMismatch.currentCount} screen{topologyMismatch.currentCount === 1 ? '' : 's'}. Detect screens and save a monitor profile for this workstation before opening the saved workspace.
              </Typography>
            </Box>
          )}

          <Divider sx={{ borderColor: '#263238' }} />
          <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 12, color: '#9fb6c2' }}>Monitor assignments</Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => setDetailsOpen(prev => !prev)}
                endIcon={detailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ color: '#90caf9', fontSize: 11, px: 0.75, py: 0.25, minHeight: 28 }}
              >
                {detailsOpen ? 'Hide monitor details' : 'Show monitor details'}
              </Button>
            </Stack>
            <Stack spacing={1}>
              {(activeProfile?.monitors || []).map((m, idx) => (
                <Box
                  key={m.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(220px, 1fr) 230px 120px' },
                    gap: 1,
                    alignItems: 'center',
                    border: '1px solid #263238',
                    bgcolor: '#151b20',
                    p: 1.25,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 13, color: '#d8e8ef' }}>{screenName(m, idx)}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#607d8b' }}>
                      {m.descriptor?.width || m.placement?.width || '?'}x{m.descriptor?.height || m.placement?.height || '?'} • position {m.descriptor?.left ?? m.placement?.left ?? '?'}, {m.descriptor?.top ?? m.placement?.top ?? '?'}
                    </Typography>
                    <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                          gap: 0.75,
                          mt: 1,
                          pt: 1,
                          borderTop: '1px solid #22313a',
                        }}
                      >
                        {monitorDetails(m).map(([label, value]) => (
                          <Box key={label}>
                            <Typography sx={{ fontSize: 10, color: '#607d8b', textTransform: 'uppercase', letterSpacing: 0 }}>{label}</Typography>
                            <Typography sx={{ ...mono, color: '#b0bec5', overflowWrap: 'anywhere' }}>{value}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={m.role || (idx === 0 ? 'primary' : 'comparison')}
                      onChange={(event) => handleRoleChange(m.id, event.target.value)}
                      sx={{
                        color: '#d8e8ef',
                        fontSize: 13,
                        '.MuiOutlinedInput-notchedOutline': { borderColor: '#35505c' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00bcd4' },
                      }}
                    >
                      {roleOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={m.role === 'disabled'}
                    onClick={() => handleOpen(m.id, m.role || 'comparison')}
                    startIcon={<OpenInNewIcon />}
                    sx={{ borderRadius: 0, minHeight: 36 }}
                  >
                    Open
                  </Button>
                </Box>
              ))}
            </Stack>
          </Box>

          {screenDetails && (
            <Box sx={{ border: '1px solid #263238', bgcolor: '#0f1418', p: 1.25 }}>
              <Typography sx={{ fontSize: 12, color: '#9fb6c2', mb: 0.5 }}>Detected screens captured</Typography>
              <Typography sx={{ fontSize: 11, color: '#607d8b' }}>
                {screenDetails.screens?.length || 0} screen{screenDetails.screens?.length === 1 ? '' : 's'} available for placement. Save the profile after choosing assignments.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ bgcolor: '#0b0f12' }}>
        <Button onClick={onClose} sx={{ color: '#90a4ae' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiMonitorDialog;
